import { CloudOffFilled } from '@brightlayer-ui/icons-mui';
import { Hero, HeroBanner, InfoListItem, ListItemTag } from '@brightlayer-ui/react-components';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import HelpCenter from '@mui/icons-material/HelpCenter';
import Info from '@mui/icons-material/Info';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import Warning from '@mui/icons-material/Warning';
import { Avatar, Divider, List, Paper, Stack, Typography } from '@mui/material';
import React, { type JSX } from 'react';
import CustomIcon from '../../../../components/CustomIcon';
import { WidgetFooter } from 'pages/WidgetManagement/common/WidgetFooter';
import { useTheme } from '@mui/material/styles';
import { CustomTransComponent, GroupPath } from 'components';
import { getListItemBackgroundColor, getListItemTextColor } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';
import { useTransformData } from 'hooks/useTransformData';
import { CustomEmptyState } from 'components/CustomEmptyState';
import { getAlarmLabelValue } from '@fiji/common/src/utils/helpers';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useTypedSelector } from '@fiji/common';
import { useRBAC } from 'hooks';

const heroTitle: any = {
    alarms: { title: 'Alarms', subtitle: 'Active' },
    offline: { title: 'Offline', subtitle: 'Active' },
    warnings: { title: 'Warning', subtitle: 'Active' },
    information: { title: 'Informational', subtitle: 'Active' },
};

const filterName: any = {
    alarms: 'Alarm Active',
    offline: 'Offline',
    warnings: 'Warning Active',
    information: 'Informational',
};

const Component = ({
    widgetData: detailWidget,
    mode,
    dropType,
}: {
    widgetData: any;
    mode: string;
    dropType?: string;
}): JSX.Element => {
    const theme: any = useTheme();

    const navigate = useNavigate();
    const { convertDateTime } = useTransformData();
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canViewTimelineDetails = hasPermission('timeline-details_view-devices');
    const canViewTimelineDetailsExpired = hasPermission('timeline-details_view-devices_hasExpired');
    const tabsData = useTypedSelector((state: any) => state.common.tabsList);
    const widgetName = detailWidget?.widgetName;
    const showSeverityCount = Boolean(detailWidget?.showSeverityCount);
    const statusCount = detailWidget?.statusCount ?? {};
    const preferences = detailWidget?.preferences ?? [];
    const secondary = detailWidget?.secondary ?? [];
    const maxCount = detailWidget?.maxCount;
    const detailRoute = detailWidget?.detailRoute;
    const isGroupContext = detailWidget?.multiDevice || dropType === 'Group' || dropType === 'Gateway';

    const getHeaderTitle = (): JSX.Element => (
        <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
            ml={1}
            sx={
                mode === 'edit'
                    ? { position: 'absolute', top: '0px', height: '48px', left: '50px' }
                    : { height: '48px', padding: '16px' }
            }
        >
            <Typography variant="subtitle2" color="primary" fontWeight={'600'} fontSize={'14px'}>
                {widgetName}
            </Typography>
        </Stack>
    );

    const getIcon = (count: number, status: string): JSX.Element => {
        if (status === 'alarms') return <NotificationsActive {...(count && { color: 'error' })} fontSize={'inherit'} />;
        if (status === 'offline') return <CloudOffFilled className={count ? 'sky-800' : ''} fontSize={'inherit'} />;
        if (status === 'warnings') return <Warning {...(count && { color: 'warning' })} fontSize={'inherit'} />;
        if (status === 'information')
            return <Info sx={{ color: count ? theme?.palette?.primary?.main : '' }} fontSize={'inherit'} />;
        return <></>;
    };

    const getRightComponent = (data: any): JSX.Element => {
        if (data?.status === 'Active' || data?.status === 'Cleared' || data?.status === 'Acknowledged') {
            return (
                <ListItemTag
                    sx={{
                        border: data?.status === 'Cleared' ? '1px solid #424E54' : 'none',
                    }}
                    label={getAlarmLabelValue(data)}
                    fontColor={getListItemTextColor(data)}
                    backgroundColor={getListItemBackgroundColor(data)}
                />
            );
        }
        return <></>;
    };
    return (
        <Paper sx={{ height: '100%', position: 'relative' }}>
            {getHeaderTitle()}
            {mode !== 'edit' && <Divider />}
            <Stack sx={{ height: '100%' }}>
                {detailWidget?.showSeverityCount && (
                    <HeroBanner className="border-bottom-1">
                        {Object.keys(statusCount).map(
                            (status: string) =>
                                preferences?.includes?.(filterName[status]) && (
                                    <Hero
                                        key={status}
                                        icon={getIcon(statusCount?.[status], status)}
                                        label={
                                            (
                                                <span>
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            fontSize: '14px',
                                                            fontWeight: 700,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {heroTitle[status]?.title}
                                                    </span>
                                                    <span
                                                        style={{
                                                            display: 'block',
                                                            fontSize: '12px',
                                                            fontWeight: 400,
                                                        }}
                                                    >
                                                        {heroTitle[status]?.subtitle}
                                                    </span>
                                                </span>
                                            ) as any
                                        }
                                        ChannelValueProps={{ value: statusCount?.[status] }}
                                    />
                                )
                        )}
                    </HeroBanner>
                )}

                <Stack
                    sx={{
                        height: `calc(100% - ${preferences.length && showSeverityCount ? 245 : 98}px)`,
                        overflow: 'auto',
                    }}
                >
                    {secondary.length ? (
                        secondary?.slice(0, maxCount ?? secondary.length).map((item: any) => (
                            <List disablePadding key={item?.id}>
                                <InfoListItem
                                    title={item?.displayName ?? item?.name ?? ''}
                                    icon={
                                        <Avatar
                                            sx={{ bgcolor: item?.icons?.status?.icon?.web?.style?.backgroundColor }}
                                        >
                                            <CustomIcon
                                                iconName={item?.icons?.status?.icon?.web?.name ?? ''}
                                                family={item?.icons?.status?.icon?.web?.family ?? ''}
                                                iconProps={{
                                                    sx: {
                                                        color: item?.icons?.status?.icon?.web?.style?.color,
                                                    },
                                                }}
                                                defaultIcon={<HelpCenter color="primary" />}
                                            />
                                        </Avatar>
                                    }
                                    rightComponent={getRightComponent(item)}
                                    statusColor={item?.icons?.status?.icon?.web?.style?.backgroundColor}
                                    {...(isGroupContext && {
                                        subtitle: [
                                            <GroupPath
                                                key={item?.id}
                                                pathName={[...(item?.groupPath ?? [])]}
                                                deviceName={item?.deviceName}
                                            />,
                                        ],
                                    })}
                                    leftComponent={
                                        <Stack>
                                            {item?.alarmUpdated ? (
                                                <>
                                                    <Typography variant="body2" fontSize={'12px'}>
                                                        {convertDateTime({
                                                            timestamp: parseInt(item?.alarmUpdated),
                                                            customFormat: 'HH:mm',
                                                        })}{' '}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {convertDateTime({
                                                            timestamp: parseInt(item?.alarmUpdated),
                                                        })}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>--</>
                                            )}
                                        </Stack>
                                    }
                                    divider="full"
                                    chevron
                                    onClick={(): void => {
                                        if (
                                            detailRoute &&
                                            mode === 'view' &&
                                            (canViewTimelineDetails || canViewTimelineDetailsExpired)
                                        )
                                            navigate(`${detailRoute}/${item.id}`);
                                    }}
                                />
                            </List>
                        ))
                    ) : (
                        <CustomEmptyState
                            className="padding-y-96"
                            icon={<FormatListBulleted fontSize={'inherit'} />}
                            title={<CustomTransComponent translationKey={'TIMELINE:NO_TIMELINE'} />}
                        />
                    )}
                </Stack>
            </Stack>

            {(canViewTimelineDetails || tabsData?.includes('timeline') || canViewTimelineDetailsExpired) && (
                <WidgetFooter
                    mode={mode}
                    ids={[]}
                    actionBtnName={<CustomTransComponent translationKey={'COMMON:VIEW_ALL_EVENTS'} />}
                    widgetData={{ ...detailWidget, viewAllButton: true }}
                />
            )}
        </Paper>
    );
};

export default Component;
